// @ts-strict-ignore

import { Mention, Message, MessageAdditionalContent, MessageID, MessageSide, MessageStatus, Reaction, Urgency, parseUrgency } from "rainbow-web-sdk/models/message.model";
import { FileDescriptor, FileState } from 'rainbow-web-sdk/models/common/fileDescriptor';
import 'moment-duration-format';
import { ContactService } from 'rainbow-web-sdk/services/core/contact.service';
import { XmppService } from 'rainbow-web-sdk/services/core/xmpp.service';
import { I18nService } from 'rainbow-web-sdk/services/core/i18n.service';
import { LoggerService } from 'rainbow-web-sdk/services/core/logger.service';
import { UtilitiesService } from "rainbow-web-sdk/services/utilities/utilities.service";
import { UrlMetaData } from "rainbow-web-sdk/services/ulrMetadata/urlMetadata.service";
import { ConversationRB } from "rainbow-web-sdk/models/conversation.model";
import { ConversationServiceRB } from "rainbow-web-sdk/services/conversation/conversation.service";

import { FileStorageService, FILE_STORAGE_SVC } from 'rainbow-web-sdk/services/files/fileStorage.service';

import type { FileServerService } from 'rainbow-web-sdk/services/files/fileServer.service';
import { FILE_SERVER_SVC } from 'rainbow-web-sdk/services/files/fileServer.service';
import { MainService } from "rainbow-web-sdk/services/core/main.service";
import { RBEvent } from "rainbow-web-sdk/models/event.model";
import xmlParser from "../../utils/xmlParser";
import { $iq } from "strophe.js";
import { formatDuration, intervalToDuration } from "date-fns";
import { SettingsService } from "rainbow-web-sdk/services/core/settings.service";
import { enUS } from "date-fns/locale";

export class ConversationServiceBulkHistoryHandler {

    private xmppService: XmppService = XmppService.getInstance();
    private mainService: MainService = MainService.getInstance();
    private i18n: I18nService = I18nService.getInstance();
    private logger: LoggerService = LoggerService.getInstance();
    private contactService: ContactService = ContactService.getInstance();
    private utilitiesService: UtilitiesService = UtilitiesService.getInstance();
    private conversationService: ConversationServiceRB = ConversationServiceRB.getInstance();
    private settingsService: SettingsService = SettingsService.getInstance();

    private fileStorageService?: FileStorageService = undefined;
    private fileServerService?: FileServerService = undefined;

    public static create(): ConversationServiceBulkHistoryHandler {
        return new ConversationServiceBulkHistoryHandler();
    }

    private constructor() {
        this.fileServerService = this.mainService.inject<FileServerService>(FILE_SERVER_SVC);
        this.fileStorageService = this.mainService.inject<FileStorageService>(FILE_STORAGE_SVC);
    }

    // **************************************************************** //
    // ** GET HISTORY PAGE STUFF                                     ** //
    // **************************************************************** //

    public async getHistoryPage(conversation: ConversationRB, nbMessage: number): Promise<void> {
        try { await this.getHistoryPageInternal(conversation, nbMessage); }
        catch (error) {
            this.logger.error(`[ConversationServiceBulkHistoryHandler] getHistoryPage -- ${error.message}`);
            //reset the current history index of case of XMPP timeout
            if (error.meesage?.indexOf("request timeout") !== -1) conversation.currentHistoryIndex = undefined;
        }
    }

    public async getHistoryPageAroundMessage(conversation: ConversationRB, messageHistoryIndex: number, nbMessages: number = 15): Promise<void> {
        this.logger.info(`[ConversationServiceBulkHistoryHandler] getHistoryPageAroundDate(${conversation.id}, ${messageHistoryIndex})`);
        await this.getHistoryPageInternal(conversation, nbMessages, 'after', messageHistoryIndex);
        await this.getHistoryPageInternal(conversation, nbMessages, 'before', messageHistoryIndex);
    }

    public async getHistoryPageFromTimeTillNow(conversation: ConversationRB, timestamp: any) {
        this.logger.info(`[ConversationServiceBulkHistoryHandler] getHistoryPage(${conversation.id}) - from ${timestamp}`);

        // Start with retrieving five message above
        await this.getHistoryPageInternal(conversation, 5, 'before', timestamp);

        // Then retrieve 250 by 250
        await this.getHistoryPageInternal(conversation, 250, 'after', timestamp);

        //as mam does not work, load the last 250 messages and that's all;
        // while (!conversation.historyBelowComplete) { await this.getHistoryPageInternal(conversation, 250, 'after', conversation.historyLastIndex); }
    }

    private async getHistoryPageInternal(conversation: ConversationRB, nbMessages: number, direction: 'after' | 'before' = 'before', messageHistoryIndex?: number) {
        this.logger.info(`[ConversationServiceBulkHistoryHandler] getHistoryPage(${conversation.id}, ${nbMessages})`);

        const resultPromise = new Promise((resolve) => { conversation.historyDefered = { resolve }; });

        // Bubble case : wait for bubble initial presence validation before sending MAM request
        if (conversation.bubble?.initPresPromise) await conversation.bubble.initPresPromise.promise;

        // Avoid duplicate request
        if (!messageHistoryIndex && conversation.currentHistoryIndex && conversation.currentHistoryIndex === conversation.historyIndex) {
            const errorMessage = `getHistoryPage(${conversation.id}, ${nbMessages}, ${conversation.historyIndex}) --- already asked`;
            this.logger.info(`[ConversationServiceBulkHistoryHandler] ${errorMessage}`);
            throw new Error(errorMessage);
        }
        conversation.currentHistoryIndex = conversation.historyIndex;

        // No request if already complete above
        if (direction === 'before' && conversation.historyAboveComplete) {
            const errorMessage = `getHistoryPage(${conversation.id}) --- historyAboveComplete`;
            this.logger.info(`[ConversationServiceBulkHistoryHandler] ${errorMessage}`);
            throw new Error(errorMessage);
        }

        // No request if already complete below
        if (direction === 'after' && conversation.historyBelowComplete) {
            const errorMessage = `getHistoryPage(${conversation.id}) --- historyBelowComplete`;
            this.logger.info(`[ConversationServiceBulkHistoryHandler] ${errorMessage}`);
            throw new Error(errorMessage);
        }

        // Create the mam request iq
        const queryId = conversation.id;
        const iqId = `${conversation.id}${this.utilitiesService.randomString(5)}`;
        const toAttr = conversation.bubble ? conversation.bubble.jid : null;
        const withField = conversation.bubble ? this.contactService.userContact.jid : conversation.id;

        const iq = $iq({ id: iqId, to: toAttr, type: 'set' })
            .c('query', { queryid: queryId, xmlns: 'urn:xmpp:mam:1:bulk' })
            .c('x', { xmlns: 'jabber:x:data', type: 'submit' })
            .c('field', { var: 'FORM_TYPE', type: 'hidden' }).c('value').t('urn:xmpp:mam:1').up().up()
            .c('field', { var: 'with' }).c('value').t(withField).up().up().up()
            .c('set', { xmlns: 'http://jabber.org/protocol/rsm' }).c('max').t(nbMessages.toString()).up();

        if (messageHistoryIndex) {
            const historyRefDate = direction === "after" ? Number(messageHistoryIndex) - 1 : messageHistoryIndex;
            iq.c(direction).t(historyRefDate.toString());
        }
        else {
            if (conversation.historyIndex === -1 || !conversation.historyIndex) { iq.c(direction).t(""); }
            else { iq.c(direction).t(conversation.historyIndex.toString()); }
        }

        const startRequestDate = performance.now();
        await this.xmppService.sendIQ(iq);
        const requestDuration = Math.round(performance.now() - startRequestDate);
        await resultPromise;

        this.logger.info(`[ConversationServiceBulkHistoryHandler] getHistoryPage(${conversation.id}) -- duration ${requestDuration}ms`);

        return conversation;
    }

    public resetHistory(conversation: ConversationRB, resetAfterSearch: boolean = false): void {
        const nbMessages = conversation.messages.length;
        // If historyIndex is -1, that means the history is not get at all, so do not update anything
        //if it's reset after search, we need to keep only the first 30 messages
        if (((nbMessages > 30 && resetAfterSearch) || nbMessages > 90) && conversation.historyIndex !== -1) {
            this.logger.info(`[ConversationServiceBulkHistoryHandler] resetHistory for conversation ${conversation.id}`);
            const nbMessageToRemove = nbMessages - 30;
            conversation.messages.splice(0, nbMessageToRemove);
            conversation.currentHistoryIndex = null;
            conversation.historyAboveComplete = false;
            conversation.historyBelowComplete = false;
            conversation.historyIndex = conversation.messages[0].historyIndex;
        }
    }

    public onHistoryMessageReceived(stanza: any) {
        try {
            this.logger.info("[conversationService] onHistoryMessageReceived");

            const stanzaElem = $(stanza);
            const resultsElem = stanzaElem.find('results');

            // Handle bulk results message
            if (resultsElem.length === 1) {
                const resultElems = resultsElem.find('result');
                if (resultElems.length > 0) {
                    const queryId = $(resultElems[0]).attr('queryid');
                    this.logger.info(`[ConversationServiceBulkHistoryHandler] onHistoryMessageReceived "results" (${queryId})`);
                    const conversation = this.conversationService.getConversationFromCache(queryId);
                    if (conversation) {
                        resultsElem.find('result').each((__index, resultElem) => {
                            const messageElem = $(resultElem).find('forwarded message');
                            const messageId = messageElem.attr('id') as MessageID;
                            const messageFrom = messageElem.attr('from');

                            const forwardedMsg = $(messageElem).find("forwarded[xmlns='urn:xmpp:forward:0']");
                            if (forwardedMsg?.length) {
                                this.logger.info("[ConversationServiceBulkHistoryHandler] (" + conversation.id + ") Forwarded Message detected");
                                const newMessageElem = $(forwardedMsg).find('forwarded message');
                                const fwdMsgdInfo = {
                                    id: newMessageElem.attr('id'),
                                    from: this.xmppService.getBareJidFromJid(newMessageElem.attr('from')),
                                    showIdentity: $(newMessageElem).find('show-identity').length > 0
                                };
                                this.createMessage(conversation, messageId, resultElem, newMessageElem, messageFrom, fwdMsgdInfo);
                            }
                            else this.createMessage(conversation, messageId, resultElem, messageElem, messageFrom);
                        });
                    }
                }

            }
            // Handle fin message
            else {
                // Get associated conversation
                const finElem = $(stanza).find("fin");
                const id = $(stanza).attr("id");
                const queryId = id.substring(0, id.length - 5);
                this.logger.info(`[ConversationServiceBulkHistoryHandler] onHistoryMessageReceived "fin" (${queryId})`);

                const conversation = this.conversationService.getConversationFromCache(queryId);
                if (conversation) {
                    conversation.historyIndex = Number.parseInt($(finElem).find("set first").text(), 10);
                    conversation.historyLastIndex = Number.parseInt($(finElem).find("set last").text(), 10);
                    if (conversation.historyIndex === conversation.historyLastIndex) {
                        conversation.historyAboveComplete = finElem.attr("complete") === "true";
                    } else {
                        conversation.historyBelowComplete = finElem.attr("complete") === "true";
                    }

                    const historyFirstMessage: Message = conversation.historyMessages[0]; // should be the oldest
                    let shouldReorder = false;

                    if (historyFirstMessage) {
                        const historyFirstMessageDate = historyFirstMessage.date.getTime();
                        // Find the index of the first message that is older
                        const index = conversation.messages.findIndex(message => message.date.getTime() > historyFirstMessageDate);
                        if (index !== -1) {
                            //check if we need to re-order messages after that
                            const historyLastMessage: Message = conversation.historyMessages[conversation.historyMessages.length - 1]; // find the newest messages from the history
                            const historyLastMessageDate = historyLastMessage.date.getTime();
                            // IF in the current messages we've a message that newer than the messages inside the conversation
                            // i.e. we've history messages before AND after the messages in the conversation, we should re-order the whole thing;
                            if (conversation.messages.findIndex(message => message.date.getTime() < historyLastMessageDate) !== -1) {
                                shouldReorder = true;
                            }

                            conversation.messages.splice(index, 0, ...conversation.historyMessages); // Insert element at the right place
                        }
                        else {
                            //we should add them at the end
                            conversation.messages.splice(conversation.messages.length, 0, ...conversation.historyMessages);
                        }
                    }

                    if (shouldReorder) {
                        //sort messages in case of mix of history and received messages not in the right order
                        conversation.messages.sort((msg1, msg2) => {
                            if (msg1.historyIndex && msg2.historyIndex) { return msg1.historyIndex - msg2.historyIndex; }
                            return 0;
                        });
                    }

                    conversation.historyMessages = [];
                    conversation.lastMessage.text = "";
                    conversation.lastMessage.sender = null;
                    if (conversation.messages && conversation.messages.length > 0) {
                        const lastMessage: Message = conversation.messages[conversation.messages.length - 1];
                        conversation.lastMessage.text = lastMessage.data;
                        conversation.lastMessage.sender = lastMessage.from.id;
                    }
                    if (conversation.historyDefered) { conversation.historyDefered.resolve(); }
                }
            }
        }
        catch (error) {
            this.logger.error("[ConversationServiceBulkHistoryHandler] onHistoryMessageReceived -- failure -- " + error);
            return true;
        }
        return true;
    }

    private createMessage(conversation: ConversationRB, messageId: MessageID, resultElem: any, messageElem: any, from: string, forwardedInfo?: any) {

        // Check message unicity
        let oldMessage: Message;
        let message: Message = conversation.getMessageById(messageId);
        if (!message) { message = conversation.historyMessages.find((item) => { return item.id === messageId; }); }
        if (!message) { message = conversation.getReplacedMessageById(messageId); }
        if (message) {
            oldMessage = message;
            this.logger.info("[ConversationServiceBulkHistoryHandler] (" + conversation.id + ") try to add an already stored message with id " + message.id);
        }

        // Try to find message contact jid from message element
        let fromContact = null;
        let type = messageElem.attr('type');
        // const from = messageElem.attr('from');
        let fromJid = from && from.indexOf('room_') === 0 ? from.split('/')[1] : this.xmppService.getBareJidFromJid(from);

        // Or from room event element
        let roomEvent = null;
        let additionalInfo;
        const eventElem = $(resultElem).find('event');
        if (!fromJid || eventElem.length) {
            if (eventElem.length) {
                fromJid = eventElem.attr('jid');
                roomEvent = eventElem.attr('name');
                if (conversation.bubble) {
                    if (conversation.bubble.isWebinarBubble()) { return; }

                    // Handle welcome special case
                    if (roomEvent === 'welcome' && conversation.bubble.owner) { fromJid = conversation.bubble.owner.jid; }

                    // Handle conferenceAdd & conferenceRemove special case
                    if ((roomEvent === 'conferenceAdd' || roomEvent === 'conferenceRemove') && conversation.bubble.owner) {
                        const bubbleUser = conversation.bubble.getUserByJid(fromJid);
                        fromContact = bubbleUser?.user ? bubbleUser.user : conversation.bubble.owner;
                    }
                    else if (roomEvent === "voicemessage") {
                        fromJid = this.contactService.userContact.jid;
                    }
                }
                type = 'admin';
            }
        }

        // Impossible to find a valid jid : ignore
        if (!fromJid) {
            this.logger.warn('[ConversationServiceBulkHistoryHandler] onHistoryMessageReceived - Receive message without valid fromJid information');
            return;
        }

        // Handle webrtc or PBX special case
        if (type === 'webrtc' || type === 'phone') {
            type = 'webrtc';
            const callerJid = $(resultElem).find("caller").text();
            fromContact = this.contactService.getContactFromCacheByJid(callerJid);
        }

        // Get or create the associated contact
        if (!fromContact) { fromContact = this.contactService.getContactFromCacheByJid(fromJid); }
        if (!fromContact) {
            // No contact found (removed contact ?)
            fromContact = this.contactService.createEmptyContact(fromJid);

            // Store this contact to not re-iterate this call
            this.contactService.contacts[fromJid] = fromContact;

            // Try to  update the contact from the server
            if (fromJid) {
                this.contactService.updateContactFromServer(fromContact).catch(() => {
                    // If the contact is not found (an auto delete guest for example)
                    // Change display name to 'unknown'
                    fromContact.displayName = this.i18n.isInitialized ? this.i18n.translate('unknown') : 'unknown';
                });
            }
        }

        // Extract message info
        const date = new Date($(resultElem).find('forwarded delay').attr('stamp'));
        let body = $(messageElem).find('body').text();
        const side = this.contactService.isUserContact(fromContact) ? MessageSide.RIGHT : MessageSide.LEFT;
        const geoloc = $(resultElem).find('geoloc');
        let geolocData = null;
        if (geoloc?.length) {
            const geolocElem = $(geoloc);
            geolocData = { lon: geolocElem.find("lon").text(), lat: geolocElem.find("lat").text() };
        }

        // Handle urgency
        let urgency: Urgency = 'std';
        const headersElem = $(messageElem).find("headers[xmlns='http://jabber.org/protocol/shim']");
        if (headersElem?.length) {
            const urgencyElem = $(headersElem).find("header[name='Urgency']");
            if (urgencyElem?.length) { urgency = parseUrgency(urgencyElem.text()); }
        }

        const oob = $(messageElem).find("x[xmlns='jabber:x:oob']");
        if (oob?.length) { type = 'fileSharing'; }

        if ($(resultElem).find("call_log").length) {
            const state = $(resultElem).find("state").text();
            if (state === "missed") { body = `missedCall||${date}`; }
            else if (state === "answered") {
                const duration: number = $(resultElem).find("duration") ? parseInt($(resultElem).find("duration").text(), 10) : 0;
                let textDuration = '';
                if (duration > 0) {
                    const durationObject = intervalToDuration({ start: 0, end: duration });
                    const formattedDuration = formatDuration(durationObject, { format: ['hours', 'minutes', 'seconds'], delimiter: ' ', zero: true, locale: enUS });

                    const currentLanguage = this.settingsService?.getAppliLanguage();
                    if (currentLanguage && currentLanguage.code === "de") {
                        textDuration = `(${formattedDuration.replace(' hours', ' H').replace(' minutes', ' min').replace(' minute', ' min').replace(' seconds', ' s').replace(' second', ' s')})`;
                    }
                    else {
                        textDuration = `(${formattedDuration.replace(' hours', 'H').replace(' hour', 'H').replace(' minutes', 'm').replace(' minute', 'm').replace(' seconds', 's').replace(' second', 's')})`;
                    }
                }
                // const textDuration = (duration > 0) ? `(${(moment.duration(duration, "ms") as any).format("h[H] mm[m] ss[s]")})` : '';
                body = `activeCallMsg||${date}||${textDuration}`;
            }
        }

        let translatedMessage = "";
        // Case of hunting group voice message in bubble
        if (roomEvent === "voicemessage") {
            //get info from message
            const gmwi = $(messageElem).find("gmwi");
            const newMsgs = gmwi.find("message").attr("new");
            const oldMsgs = gmwi.find("message").attr("old");
            translatedMessage = this.i18n.translate("huntingGroupVmCounters", { newMsgs, oldMsgs });
        }

        const pinElem = $(resultElem).find("pin");
        if (pinElem?.length) {
            roomEvent = "pin";
            type = "pin";
        }

        const recapElem = $(resultElem).find("recap");
        if (recapElem?.length) {
            roomEvent = "recap";
            type = "recap";
            additionalInfo = { recapId: $(resultElem).find("recap").attr("id") };
        }

        if (roomEvent === ("pollPublish")) {
            type = "poll";
        }

        if (roomEvent === "pollPublish" || roomEvent === "pollUnpublish" || roomEvent === "pollTerminated" || roomEvent === "pin" || roomEvent === 'recap') {
            const isMe = this.contactService.isUserContact(fromContact);
            if (isMe) {
                translatedMessage = this.i18n.translate(roomEvent + "MsgRoomMine");
            } else {
                translatedMessage = this.i18n.translate(roomEvent + "MsgRoom", { userDisplayName: fromContact.displayName });
            }
        }

        // Handle urlMetadata
        const urlMetadataElems = $(resultElem).find("url-metadata");
        const urlMetadata = [];
        if (urlMetadataElems) {
            urlMetadataElems.each((__index, urlMetadataElem) => {
                const data = $(urlMetadataElem);
                urlMetadata.push(UrlMetaData.create({ title: data.attr('title'), url: data.attr('url'), description: data.attr('description'), image: data.attr('image'), urlOnly: data.attr('urlOnly') as `${boolean}` }));
            });
        }

        // Handle message reactions
        const reactionsElem = xmlParser(resultElem).find("reactions[xmlns='urn:xmpp:reaction']");
        let reactions: Map<string, Reaction>;
        let reactionsCount: Map<string, number>;
        if (reactionsElem?.length) {
            const reactionElems = reactionsElem.find('reaction');
            if (reactionElems?.length) {
                reactions = this.conversationService.processStanzaReaction(reactionElems);
            }

            const reactionsCountElem = reactionsElem.find('count');
            if (reactionsCountElem?.length) {
                reactionsCount = this.conversationService.processStanzaReactionsCount(reactionsCountElem);
            }

        }

        // Create new message
        switch (type) {
            case 'webrtc': message = Message.createWebRTCMessage(messageId, date, fromContact, side, body, false); break;
            case 'admin': message = Message.createBubbleAdminMessage(messageId, date, fromContact, roomEvent, translatedMessage); break;
            case 'pin': message = Message.createPinMessage(messageId, date, fromContact, side, translatedMessage); break;
            case 'recap': {
                const additionalContent = MessageAdditionalContent.create("recapId", additionalInfo?.recapId);
                message = Message.createShareRecapMessage(messageId, date, fromContact, side, translatedMessage, additionalContent); break;
            }
            case 'poll':
            {
                // retrieve poll id and send it as additionalContent
                const pollInfo = $(resultElem).find('poll-info');
                const additionalContent = MessageAdditionalContent.create("pollId", pollInfo[0]?.id);
                message = Message.createPollMessage(messageId, date, fromContact, side, translatedMessage, additionalContent); break;
            }
            case 'fileSharing': message = this.createFileSharingMessage(oob, messageId, date, fromContact, side, body, geolocData); break;
            case 'recording': break;
            default:
                {
                    // Manage additional content
                    const contents = $(resultElem).find("content[xmlns='urn:xmpp:content']");
                    let additionalContent: MessageAdditionalContent = null;
                    if (contents && contents.length > 0) {
                        const contentElem = $(contents[0]);
                        additionalContent = MessageAdditionalContent.create(contentElem.attr('type'), contentElem.text());
                    }

                    // Manage mentions
                    let mentions: Mention[] = [];
                    const mentionElem = xmlParser(resultElem).find("mention[xmlns='urn:xmpp:attention:0']");
                    if (mentionElem.length) {
                        const result = this.conversationService.processStanzaMentions(body, mentionElem);
                        mentions = result.mentions;
                        body = result.body;
                    }

                    // Manage replacement content
                    const replace = $(resultElem).find('replace');
                    if (replace?.length) {
                        const replacedMsgId = replace.attr("id") as MessageID;
                        let replacedMessage = conversation.getMessageByIdInHistory(replacedMsgId);

                        if (!replacedMessage) {
                            replacedMessage = conversation.getMessageById(replacedMsgId);
                        }

                        if (!replacedMessage) {
                            replacedMessage = Message.create(replacedMsgId, date, fromContact, side, body, additionalContent, null, null, null, mentions);
                            message = replacedMessage;
                        }
                        if (fromContact.jid !== replacedMessage.from.jid) {
                            this.logger.warn("[ConversationServiceBulkHistoryHandler] onHistoryMessageReceived : message Replaced by somebody else - Skip");
                            message = null;
                        }
                        else if (replacedMessage) {
                            replacedMessage.addReplaceMsg(messageId, body);
                            replacedMessage.mentions = mentions;
                            if (reactionsCount && reactions) {
                                replacedMessage.handleReactionsCount(reactionsCount);
                                replacedMessage.handleReactions(reactions);
                            }
                            const logBody = body.length ? body.substring(0, 1) + "***" + body.substring(body.length - 1) : "";
                            this.logger.info("Add new history replace message for message " + messageId + " witn body " + logBody);
                        }
                    }
                    else {
                        // Standard message (no replacement)
                        const answeredMsg = $(resultElem).find('answeredMsg');
                        let answeredMsgId: MessageID | null = null;
                        let answeredMsgDate: Date | null = null;

                        if (answeredMsg) {
                            answeredMsgId = answeredMsg.text() as MessageID;

                            const answerMsgTimestamp = parseInt(answeredMsg.attr('stamp'));
                            answeredMsgDate = new Date(answerMsgTimestamp);
                        }

                        message = Message.create(messageId, date, fromContact, side, body, additionalContent, null, answeredMsgId, answeredMsgDate, mentions);
                        message.urgency = urgency;
                        message.urlMetadata = urlMetadata.length > 0 ? urlMetadata[0] : null;

                        const modified = $(resultElem).find('modified');
                        if (modified?.length) message.modified = true;

                        const deleted = $(resultElem).find("deleted");
                        if (deleted?.length) message.deleted = true;

                        if (answeredMsgId) {
                            const answeredMessage: Message = conversation.getMessageByIdInHistory(answeredMsgId);
                            if (answeredMessage) {
                                answeredMessage.urgencyAck = true;
                                answeredMessage.sendUpdateEvent();
                            } else {
                                this.logger.warn("[ConversationServiceBulkHistoryHandler] answeredMessage message not found, no action done on it");
                            }
                        }

                    }
                }
                break;
        }

        if (message) {
            message.isForwarded = forwardedInfo !== undefined;
            if (message.isForwarded) {
                message.forwardedInfo = forwardedInfo;
                message.showIdentity = forwardedInfo.showIdentity;
            }

            if (reactionsCount && reactions) {
                message.handleReactionsCount(reactionsCount);
                message.handleReactions(reactions);
            }

            message.urgency = urgency;
            message.historyIndex = $(resultElem).attr('id');
            let ack = $(messageElem).find('ack');
            //if ack is not found in the current element, check the parent for ack
            if (!ack.length) { ack = $(resultElem).find('ack'); }
            const readValue = (message.side === MessageSide.RIGHT && !$(ack).attr("readtime") && $(ack).attr('read') === "true") ? MessageStatus.UNREAD : MessageStatus.READ;
            message.receiptStatus = $(ack).attr('read') === "true" ? readValue : ($(ack).attr("recv") === "true" ? MessageStatus.UNREAD : MessageStatus.SENT);

            if (oldMessage) {
                message.rxSubject = oldMessage.rxSubject;
                message.isMerged = oldMessage.isMerged;
                message.lastReadSubject = oldMessage.lastReadSubject;
                oldMessage = message;
                let index = -1;
                conversation.messages.forEach((msg, ind) => {
                    if (msg.id === messageId) {
                        index = ind;
                    }
                });
                if (index !== -1) { conversation.messages[index] = oldMessage; }

                oldMessage.sendUpdateEvent(RBEvent.create('updateMessage', oldMessage));
            }
            else {
                if (message.isAdaptiveCardResponse()) {
                    const messageObject = JSON.parse(message.additionalContent.message);
                    if (!messageObject || messageObject.rainbow.text !== '') conversation.addMessageInHistoryMessages(message);
                }
                else conversation.addMessageInHistoryMessages(message);
                this.logger.info("Add new history message : " + message.getInfoForLogs());
            }

        }
    }

    private createFileSharingMessage(oob: any, messageId: MessageID, date: Date, from: any, side: MessageSide, body: string, geoloc: any): Message {
        if (!this.fileStorageService) return null;

        const oobElem = $(oob);
        const url = oobElem.find("url").text();
        const fileName = oobElem.find("filename").text();
        const fileId = this.fileStorageService.extractFileIdFromUrl(url);
        const message = Message.createFileSharingMessage(messageId, date, from, side, body, fileId, fileName, null, geoloc);

        this.fileStorageService.retrieveAndStoreOneFileDescriptor(fileId)
            .then((fileDescriptor: FileDescriptor) => {
                if (fileDescriptor && fileDescriptor.state === FileState.NOT_UPLOADED) {
                    message.receiptStatus = MessageStatus.ERROR;
                    message.fileErrorMsg = this.i18n.translate('file_notuploaded');
                }

                this.fileServerService.getBlobThumbnailFromFileDescriptor(fileDescriptor)
                    .then((blob: any) => {
                        message.sendUpdateEvent();
                        fileDescriptor.previewBlob = blob;
                        fileDescriptor.previewBlobSubject.next(fileDescriptor.previewBlob);
                    }).catch(() => {
                        this.logger.info("[ConversationServiceBulkHistoryHandler] getBlobThumbnailFromFileDescriptor error ");
                        fileDescriptor.previewBlobSubject.next(fileDescriptor.previewBlob);
                    });

            })
            .catch((error) => {
                this.logger.info("[ConversationServiceBulkHistoryHandler] createFileSharingMessage error " + error);
            });

        return message;
    }

    /**
     * Find message by ID via MAM IQ request
     */

    public retriveMessageInConversation(conversation: ConversationRB, msgDate: any, messageId: MessageID): Promise<void> {
        return new Promise((resolve, reject) => {
            // Do nothing for userContact
            if (conversation.contact && this.contactService.isUserContact(conversation.contact)) reject();

            const queryId = `cache_${conversation.id}${this.utilitiesService.randomString(5)}`;

            this.sendRetriveMessageInConversationToMam(queryId, conversation, 1, msgDate, "around", messageId,
                () => {
                    this.logger.info(`[ConversationServiceBulkHistoryHandler] retriveMessageInConversation ${messageId} -- finished`);
                    resolve();
                }, () => {
                    this.logger.warn(`[ConversationServiceBulkHistoryHandler] retriveMessageInConversation ${messageId} -- failed`);
                    reject();
                });
        });
    }

    private sendRetriveMessageInConversationToMam(queryId, conversation, size, msgDate, order, messageId: MessageID, finishMethod, onError) {
        this.logger.info(`[ConversationServiceBulkHistoryHandler] sendRetriveMessageInConversationToMam ${queryId} ${messageId} ${msgDate}`);
        // Request for history messages for the room chat
        if (conversation.bubble) {
            const mamRequest = {
                queryid: queryId,
                with: this.contactService.userContact.jid,
                max: size,
                msg_id: messageId,
                onComplete: finishMethod,
                onError: onError
            };
            mamRequest[order] = msgDate;

            this.xmppService.connection.mam.querymuc(queryId, conversation.bubble.jid, mamRequest);
        } else {
            const mamRequest = {
                queryid: queryId,
                with: conversation.id,
                max: size,
                msg_id: messageId,
                onComplete: finishMethod,
                onError: onError
            };
            mamRequest[order] = msgDate;

            // Request for history messages for the conversation
            this.xmppService.connection.mam.query(queryId, mamRequest);
        }
    }

    public onRetrieveMessageInConversationReceived(stanza: any): boolean {
        try {
            const stanzaElem = $(stanza);
            const queryId = stanzaElem.find("result").attr("queryid");
            if (queryId?.startsWith("cache_")) {

                let conversationId = queryId.substring(6);
                conversationId = conversationId.substring(0, conversationId.length - 5);

                const conversation = this.conversationService.getConversationFromCache(conversationId);
                if (conversation) {

                    // Extract fromJid
                    let fromJid;
                    const brutJid = stanzaElem.find("forwarded message").attr("from");

                    if (brutJid && brutJid.indexOf("room_") === 0) {
                        fromJid = brutJid.split("/")[1];
                    } else {
                        fromJid = this.xmppService.getBareJidFromJid(stanzaElem.find("forwarded message").attr("from"));
                    }

                    if (!fromJid) {
                        this.logger.warn(`[ConversationServiceBulkHistoryHandler] onRetrieveMessageInConversationReceived ${queryId} - Receive message without valid fromJid information`);
                        return true;
                    }

                    let from = this.contactService.getContactFromCacheByJid(fromJid);
                    const messageId = stanzaElem.find("forwarded message").attr("id") as MessageID;
                    const date = new Date(stanzaElem.find("forwarded delay").attr("stamp"));
                    const body = stanzaElem.find("forwarded message body").text();
                    const side = this.contactService.isUserContact(from) ? MessageSide.RIGHT : MessageSide.LEFT;

                    if (!from) {
                        this.logger.warn(`[ConversationServiceBulkHistoryHandler] onRetrieveMessageInConversationReceived ${queryId} missing contact for jid ${fromJid}, ignore message`);
                        from = this.contactService.createEmptyContact(fromJid);
                    }

                    this.logger.info(`[ConversationServiceBulkHistoryHandler] onRetrieveMessageInConversationReceived : conversation with query ${queryId} and message ${messageId} -- found`);

                    const message = Message.create(messageId, date, from, side, body);
                    if (message) conversation.addMessageInCacheMessages(message);
                }
                else {
                    this.logger.warn(`[ConversationServiceBulkHistoryHandler] onRetrieveMessageInConversationReceived : conversation with ${queryId} -- NOT found`);
                }
            }
            return true;
        }
        catch (error) {
            this.logger.error("[ConversationServiceBulkHistoryHandler] onRetrieveMessageInConversationReceived error -- " + error);
            return true;
        }

    }
}
